import * as moment from 'moment';
import { DaysOnHandRequired, ReorderDetails } from '.';
import { ProductItemDohUserData } from '../store/order-wizard/order-wizard-data/order-wizard-data.model';

// API Models

export interface ApiDatesOfServiceRequestItem {
  itemId: number;
  uiId?: string;
  chargeType?: string;
  daysOnHand?: number | null;
  dohRequired?: boolean;
}

export interface ApiDatesOfServiceRequest {
  patientId: number;
  shipmentType: number;
  overrideTSD?: null; // TODO set when we auto delay reorders
  items: ApiDatesOfServiceRequestItem[];
}

export interface ApiDatesOfServiceResponseItem {
  itemId: number;
  uiId?: string;
  shippingFrequency?: number;
  calculatedDos?: string;
  lastDos?: string;
  targetedShipDate?: string;
  consentDate?: string;
}

export enum DosResult {
  Consent = 'CONSENT',
  End = 'END',
  Edit = 'EDIT',
}

export interface ApiDatesOfServiceResponse {
  dosResult: DosResult;
  earliestOrderCanShip?: string;
  items: ApiDatesOfServiceResponseItem[];
}

// FE Models

export interface ItemDatesOfService {
  itemId: number;
  uiId?: string;
  shippingFrequencyInMonths?: number;
  calculatedDos?: string;
  lastDos?: string;
  targetedShipDate?: string;
  consentDate?: string;
}

export interface DatesOfService {
  items: ItemDatesOfService[];
  dosResult: DosResult;
  earliestOrderCanShip?: string;
  // Computed
  hasMissingItems: boolean;
  hasUnknownItemDates: boolean;
  hasDifferentItemShipDates: boolean;
  hasFutureItemConsentDates: boolean;
}

// Mappers

export const buildDatesOfServiceRequest = (
  patientId: number,
  reorder: ReorderDetails,
  daysOnHandRequired: DaysOnHandRequired[],
  daysOnHandUserData?: ProductItemDohUserData[]
) => {
  return {
    patientId,
    shipmentType: reorder.shipmentTypeId!,
    overrideTSD: null,
    items: reorder.items.map((item) => {
      const dohRequirement = daysOnHandRequired.find((dohItem) => dohItem.itemId === item.id);
      const dohUserData = daysOnHandUserData?.find((dohUdItem) => dohUdItem.itemId === item.id);
      return {
        itemId: item.id,
        uiId: item.uiId,
        chargeType: item.chargeType,
        daysOnHand: dohUserData ? dohUserData.doh : dohRequirement?.estimatedDaysOnHand,
        dohRequired: dohRequirement?.daysOnHandRequired,
      };
    }),
  };
};

export const mapDatesOfService = (res: ApiDatesOfServiceResponse, reorder: ReorderDetails): DatesOfService => {
  const hasMissingItems = reorder.items.some((i) => !res.items.find((dosItem) => dosItem.itemId === i.id));
  const hasUnknownItemDates = res.items.some((i) => !i.consentDate || !i.targetedShipDate);

  const targetedShipDates = [...res.items.map((i) => i.targetedShipDate)]
    .filter((dt) => !!dt)
    .map((dt) => moment(dt).startOf('day'));

  const hasDifferentItemShipDates =
    targetedShipDates.length > 0 && targetedShipDates.some((dt) => !dt.isSame(targetedShipDates[0]));

  const consentDates = res.items.map((i) => i.consentDate);
  const today = moment().startOf('day');
  const hasFutureItemConsentDates =
    !hasUnknownItemDates && consentDates.some((date) => moment(date).startOf('day').isAfter(today));

  const now = moment();

  return {
    items: res.items.map((i) => ({
      itemId: i.itemId,
      uiId: i.uiId,
      shippingFrequencyInMonths: i.shippingFrequency,
      calculatedDos: i.calculatedDos,
      lastDos: i.lastDos,
      targetedShipDate: i.targetedShipDate,
      consentDate: i.consentDate,
    })),
    dosResult: res.dosResult,
    earliestOrderCanShip:
      res.earliestOrderCanShip &&
      (now.isAfter(moment(res.earliestOrderCanShip), 'day') ? now.format('YYYY-MM-DD') : res.earliestOrderCanShip),
    hasMissingItems,
    hasUnknownItemDates,
    hasDifferentItemShipDates,
    hasFutureItemConsentDates,
  };
};
